import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MohBalance, PayAppMohInput } from 'src/app/shared/models';

export interface CreatePayAppDialogData {
  itemCount: number;
  startDate: string;
  endDate: string;
  totalAmount: number;
  payappName: string;
  // Items breakdown
  readyItems?: { count: number; total: number };
  deferredItems?: { count: number; total: number };
  rejectedItems?: { count: number; total: number };
  // Billing settings from project
  billingSettings?: {
    retainage_percentage: number;
    liquidated_damages_per_day: number;
    current_retainage_held?: number;
    days_past_contract_end?: number;
    days_remaining?: number;
    default_template_id?: string;
  };
  // Use simpler legacy dialog
  useLegacyLayout?: boolean;
  enableMohPerItem?: boolean;
  mohBalances?: MohBalance[];
  installedByItemCode?: Record<string, number>;
  validateBeforeClose?: (result: CreatePayAppDialogResult) => Promise<CreatePayAppDialogValidationResult>;
}

export interface CreatePayAppDialogResult {
  payappName: string;
  retainage_released: number;
  liquidated_damages_days: number;
  material_on_hand: number;
  moh?: PayAppMohInput[];
  acknowledged_warnings?: { code: string; bid_item_id: string; comment: string }[];
}

export interface CreatePayAppDialogValidationResult {
  blocks?: CreatePayAppDialogValidationBlock[];
  acknowledged_warnings?: { code: string; bid_item_id: string; comment: string }[];
  errorMessage?: string;
}

export interface CreatePayAppDialogValidationBlock {
  code: string;
  bid_item_id: string;
  message: string;
  details?: {
    G?: number;
    F?: number;
    contract?: number;
    quantity_to_date?: number;
    adjusted_quantity?: number;
  };
}

@Component({
    selector: 'app-create-payapp-dialog',
    templateUrl: './create-payapp-dialog.component.html',
    styleUrls: ['./create-payapp-dialog.component.scss'],
    standalone: false
})
export class CreatePayappDialogComponent implements OnInit {
  payappForm: UntypedFormGroup;
  showAdditionalEntries = false;
  showMohEntries = false;
  totalMohAdvanced = 0;
  totalMohDrawdown = 0;
  totalMohChange = 0;
  totalMohClosing = 0;
  mohSearchControl = new FormControl('');
  mohRowErrors: Record<string, string[]> = {};
  blockedMohItemIds = new Set<string>();
  private mohContractChecks: Record<string, { completed: number; contract: number }> = {};
  @ViewChild('mohSection') mohSection?: ElementRef<HTMLElement>;
  validationError = '';
  validating = false;

  // Summary calculations
  workCompletedThisPeriod = 0;
  totalEarnings = 0;
  retainageAmount = 0;
  retainageReleased = 0;
  liquidatedDamagesAmount = 0;
  netDeductions = 0;
  amountDueThisPeriod = 0;

  constructor(
    public dialogRef: MatDialogRef<CreatePayappDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreatePayAppDialogData,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.calculateSummary();
  }

  get useLegacyLayout(): boolean {
    return this.data.useLegacyLayout === true && !this.enableMohPerItem;
  }

  private initForm(): void {
    // In legacy mode, payapp name is optional
    const nameValidators = this.useLegacyLayout ? [] : [Validators.required];

    this.payappForm = this.fb.group({
      payappName: [this.data.payappName || '', nameValidators],
      material_on_hand: [0, [Validators.min(0)]],
      retainage_released: [0, [Validators.min(0)]],
      liquidated_damages_days: [0, [Validators.min(0)]],
      moh: this.fb.array([]),
    });

    if (this.enableMohPerItem) {
      this.buildMohRows();
    }

    // Recalculate summary when form values change
    this.payappForm.valueChanges.subscribe(() => {
      this.calculateSummary();
    });
  }

  get enableMohPerItem(): boolean {
    return this.data.enableMohPerItem === true;
  }

  get mohRows(): UntypedFormArray {
    return this.payappForm.get('moh') as UntypedFormArray;
  }

  get filteredMohRows(): { control: UntypedFormGroup; index: number }[] {
    const search = this.mohSearchValue;
    return this.mohRows.controls
      .map((control, index) => ({ control: control as UntypedFormGroup, index }))
      .filter(({ control }) => {
        if (!search) {
          if (this.blockedMohItemIds.size) {
            return this.blockedMohItemIds.has(control.get('bid_item_id')?.value);
          }
          return this.mohRowNeedsAttention(control);
        }
        const itemCode = String(control.get('item_code')?.value || '').toLowerCase();
        const description = String(control.get('description')?.value || '').toLowerCase();
        return itemCode.includes(search) || description.includes(search);
      });
  }

  get editedMohRows(): { control: UntypedFormGroup; index: number }[] {
    return this.mohRows.controls
      .map((control, index) => ({ control: control as UntypedFormGroup, index }))
      .filter(({ control }) => this.mohRowHasUserEntry(control));
  }

  get hasMohSearch(): boolean {
    return this.mohSearchValue.length > 0;
  }

  get mohSearchHint(): string {
    const shownCount = this.filteredMohRows.length;
    const totalCount = this.mohRows.length;
    if (this.hasMohSearch) {
      return `Searching all ${totalCount} bid items`;
    }
    if (this.blockedMohItemIds.size) {
      return `Showing ${shownCount} items that need material on hand review. Search to view other bid items.`;
    }
    return `Showing ${shownCount} items with stored balance or installed work. Search all ${totalCount} bid items to add an advance.`;
  }

  private get mohSearchValue(): string {
    return (this.mohSearchControl.value || '').toLowerCase().trim();
  }

  private mohRowNeedsAttention(control: UntypedFormGroup): boolean {
    const bidItemId = control.get('bid_item_id')?.value;
    const hasValidationError = Boolean(bidItemId && (this.mohRowErrors[bidItemId] || []).length > 0);
    return (parseFloat(control.get('opening_balance')?.value) || 0) > 0
      || (parseFloat(control.get('installed_this_period')?.value) || 0) > 0
      || this.mohRowHasUserEntry(control)
      || hasValidationError;
  }

  private mohRowHasUserEntry(control: UntypedFormGroup): boolean {
    return (parseFloat(control.get('advance_added')?.value) || 0) > 0
      || (parseFloat(control.get('drawdown')?.value) || 0) > 0;
  }

  private buildMohRows(): void {
    const balances = this.data.mohBalances || [];
    balances.forEach(balance => {
      const installedThisPeriod = this.data.installedByItemCode?.[balance.item_code] || 0;
      this.mohRows.push(this.fb.group({
        bid_item_id: [balance.bid_item_id],
        item_code: [balance.item_code],
        description: [balance.description],
        adjusted_contract_amount: [balance.adjusted_contract_amount || 0],
        amount_billed_to_date: [balance.amount_billed_to_date || 0],
        opening_balance: [balance.moh_on_hand || 0],
        installed_this_period: [installedThisPeriod],
        advance_added: [0, [Validators.min(0)]],
        drawdown: [0, [Validators.min(0)]],
        closing_balance: [balance.moh_on_hand || 0],
      }));
    });
  }

  clearZeroMohAmount(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (Number(input.value) === 0) input.value = '';
  }

  normalizeMohAmount(index: number, field: 'advance_added' | 'drawdown', event: FocusEvent): void {
    const control = this.mohRows.at(index).get(field);
    if (control.value == null) control.setValue(0, { emitEvent: false });
    this.onMohValueChange(index);
    (event.target as HTMLInputElement).value = String(control.value);
  }

  maxMohDrawdown(index: number): number {
    const row = this.mohRows.at(index);
    const available = (parseFloat(row.get('opening_balance')?.value) || 0)
      + (parseFloat(row.get('advance_added')?.value) || 0);
    const installed = parseFloat(row.get('installed_this_period')?.value) || 0;
    return Math.max(0, Math.round(Math.min(available, installed) * 100) / 100);
  }

  useMaxMohDrawdown(index: number): void {
    const drawdown = this.mohRows.at(index).get('drawdown');
    drawdown.setValue(this.maxMohDrawdown(index), { emitEvent: false });
    drawdown.markAsDirty();
    this.onMohValueChange(index);
  }

  onMohValueChange(index: number): void {
    const row = this.mohRows.at(index) as UntypedFormGroup;
    const bidItemId = row.get('bid_item_id')?.value;
    const opening = parseFloat(row.get('opening_balance')?.value) || 0;
    const advance = parseFloat(row.get('advance_added')?.value) || 0;
    const requestedDrawdown = parseFloat(row.get('drawdown')?.value) || 0;
    const maxDrawdown = this.maxMohDrawdown(index);
    const drawdown = Math.max(0, Math.min(requestedDrawdown, maxDrawdown));

    if (drawdown !== requestedDrawdown) {
      row.get('drawdown')?.setValue(drawdown, { emitEvent: false });
    }
    if (bidItemId) {
      delete this.mohRowErrors[bidItemId];
    }
    this.validationError = '';
    row.get('closing_balance')?.setValue(opening + advance - drawdown, { emitEvent: false });
    this.calculateSummary();
  }

  clearMohRow(index: number): void {
    const row = this.mohRows.at(index) as UntypedFormGroup;
    const bidItemId = row.get('bid_item_id')?.value;
    row.get('advance_added')?.setValue(0, { emitEvent: false });
    row.get('drawdown')?.setValue(0, { emitEvent: false });
    row.get('closing_balance')?.setValue(row.get('opening_balance')?.value || 0, { emitEvent: false });
    if (bidItemId) {
      delete this.mohRowErrors[bidItemId];
    }
    this.validationError = '';
    this.calculateSummary();
  }

  private calculateSummary(): void {
    const formValue = this.payappForm.value;
    const retainagePercent = this.data.billingSettings?.retainage_percentage ?? 5;
    const ldRate = this.data.billingSettings?.liquidated_damages_per_day || 0;

    // Work completed this period (ready items total)
    this.workCompletedThisPeriod = this.data.readyItems?.total || this.data.totalAmount || 0;

    this.totalMohAdvanced = this.mohRows.controls.reduce((sum, ctrl) => sum + (parseFloat(ctrl.get('advance_added')?.value) || 0), 0);
    this.totalMohDrawdown = this.mohRows.controls.reduce((sum, ctrl) => sum + (parseFloat(ctrl.get('drawdown')?.value) || 0), 0);
    this.totalMohChange = this.totalMohAdvanced - this.totalMohDrawdown;
    this.totalMohClosing = this.enableMohPerItem
      ? this.mohRows.controls.reduce((sum, ctrl) => sum + (parseFloat(ctrl.get('closing_balance')?.value) || 0), 0)
      : parseFloat(formValue.material_on_hand) || 0;

    // Total earnings
    const materialThisPeriod = this.enableMohPerItem ? this.totalMohChange : this.totalMohClosing;
    this.totalEarnings = this.workCompletedThisPeriod + materialThisPeriod;

    // Retainage calculation
    this.retainageAmount = this.totalEarnings * (retainagePercent / 100);

    // Retainage released
    this.retainageReleased = parseFloat(formValue.retainage_released) || 0;

    // Liquidated damages
    const ldDays = parseInt(formValue.liquidated_damages_days, 10) || 0;
    this.liquidatedDamagesAmount = ldDays * ldRate;

    // Net deductions
    this.netDeductions = this.retainageAmount - this.retainageReleased + this.liquidatedDamagesAmount;

    // Amount due this period
    this.amountDueThisPeriod = this.totalEarnings - this.netDeductions;
  }

  getDateRangeMessage(): string {
    if (this.data?.startDate && this.data?.endDate) {
      return `from ${this.data.startDate} to ${this.data.endDate}`;
    } else if (this.data?.startDate) {
      return `starting from ${this.data.startDate}`;
    } else if (this.data?.endDate) {
      return `up to ${this.data.endDate}`;
    } else {
      return '';
    }
  }

  get retainagePercentage(): number {
    return this.data.billingSettings?.retainage_percentage ?? 5;
  }

  get liquidatedDamagesRate(): number {
    return this.data.billingSettings?.liquidated_damages_per_day || 0;
  }

  get currentRetainageHeld(): number {
    return this.data.billingSettings?.current_retainage_held || 0;
  }

  get contractTimeStatus(): { message: string; type: 'info' | 'warning' } {
    const daysPastEnd = this.data.billingSettings?.days_past_contract_end;
    const daysRemaining = this.data.billingSettings?.days_remaining;

    if (daysPastEnd && daysPastEnd > 0) {
      return {
        message: `Project is ${daysPastEnd} days past contract end date`,
        type: 'warning'
      };
    } else if (daysRemaining !== undefined) {
      return {
        message: `Project is within contract time (${daysRemaining} days remaining)`,
        type: 'info'
      };
    }
    return { message: '', type: 'info' };
  }

  async onConfirmClick(): Promise<void> {
    if (this.validating) return;
    if (this.payappForm.invalid) {
      this.payappForm.markAllAsTouched();
      return;
    }
    const formValue = this.payappForm.value;
    const result: CreatePayAppDialogResult = {
      payappName: formValue.payappName.trim(),
      retainage_released: parseFloat(formValue.retainage_released) || 0,
      liquidated_damages_days: parseInt(formValue.liquidated_damages_days, 10) || 0,
      material_on_hand: this.enableMohPerItem ? 0 : parseFloat(formValue.material_on_hand) || 0,
      moh: this.enableMohPerItem ? this.mohRows.controls
        .map((ctrl) => ({
          bid_item_id: ctrl.get('bid_item_id')?.value,
          advance_added: parseFloat(ctrl.get('advance_added')?.value) || 0,
          drawdown: parseFloat(ctrl.get('drawdown')?.value) || 0,
        }))
        .filter(row => row.advance_added > 0 || row.drawdown > 0) : undefined,
    };

    if (this.enableMohPerItem && this.data.validateBeforeClose) {
      this.validating = true;
      this.validationError = '';
      this.mohRowErrors = {};
      try {
        const validation = await this.data.validateBeforeClose(result);
        if (validation?.blocks?.length) {
          this.applyMohBlocks(validation.blocks);
          return;
        }
        if (validation?.errorMessage) {
          this.validationError = validation.errorMessage;
          this.showMohReview();
          return;
        }
        result.acknowledged_warnings = validation?.acknowledged_warnings;
      } catch {
        this.validationError = 'Unable to validate material on hand entries';
        this.showMohReview();
        return;
      } finally {
        this.validating = false;
      }
    }

    this.dialogRef.close(result);
  }

  getMohRowErrors(control: UntypedFormGroup): string[] {
    const bidItemId = control.get('bid_item_id')?.value;
    const errors = bidItemId ? [...(this.mohRowErrors[bidItemId] || [])] : [];
    const check = this.mohContractChecks[bidItemId];
    if (check) {
      const closing = parseFloat(control.get('closing_balance')?.value) || 0;
      const total = Math.round((check.completed + closing) * 100) / 100;
      if (total > check.contract) {
        errors.push(this.formatMohBlockMessage({ code: 'OVER_CONTRACT_AMOUNT', bid_item_id: bidItemId,
          message: '', details: { G: total, contract: check.contract } }));
      }
    }
    return errors;
  }

  private applyMohBlocks(blocks: CreatePayAppDialogValidationBlock[]): void {
    const errors: Record<string, string[]> = {};
    blocks.forEach(block => {
      if (!block.bid_item_id) {
        return;
      }
      errors[block.bid_item_id] = errors[block.bid_item_id] || [];
      if (block.code === 'OVER_CONTRACT_AMOUNT' && block.details?.G != null && block.details?.contract != null) {
        const row = this.mohRows.controls.find(control => control.get('bid_item_id')?.value === block.bid_item_id);
        const closing = block.details.F ?? (parseFloat(row?.get('closing_balance')?.value) || 0);
        this.mohContractChecks[block.bid_item_id] = {
          completed: block.details.G - closing, contract: block.details.contract,
        };
      } else {
        errors[block.bid_item_id].push(this.formatMohBlockMessage(block));
      }
    });
    this.mohRowErrors = errors;
    this.blockedMohItemIds = new Set(Object.keys(errors));
    this.mohSearchControl.setValue('');
    this.showMohReview();
  }

  private showMohReview(): void {
    this.showMohEntries = true;
    setTimeout(() => this.mohSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  private formatMohBlockMessage(block: CreatePayAppDialogValidationBlock): string {
    if (block.code === 'OVER_CONTRACT_AMOUNT' && block.details?.G != null && block.details?.contract != null) {
      return `Stored and completed to date exceed the contract by ${this.formatCurrency(block.details.G - block.details.contract)}. If stored materials were installed this period, enter their value under Drawdown.`;
    }
    if (block.code === 'OVER_CONTRACT_QTY' && block.details?.quantity_to_date != null && block.details?.adjusted_quantity != null) {
      return `Installed quantity would exceed contract quantity: ${block.details.quantity_to_date} / ${block.details.adjusted_quantity}`;
    }
    return block.message || 'Material on hand entry is blocked';
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
